import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

class StickyAddToCartComponent extends Component {
  // Let op: productImage is optioneel (alleen als er een afbeelding is gerenderd)
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  #buyButtonsIntersectionObserver = null;
  #footerIntersectionObserver = null;

  #resetTimeout;
  #scrollClickTimeout;

  #isStuck = false;
  #hiddenByBottom = false;

  #abortController = new AbortController();

  #targetAddToCartButton = null;
  #currentQuantity = 1;

  #variantImages = null;
  #variantIdObserver = null;

  #variants = null;
  #stickySelectsBound = false;

  // Extra: keep variant id in sync even if value-attribute doesn't change
  #variantSyncInterval = null;

  connectedCallback() {
    super.connectedCallback();

    this.#ensureStickyBarInBody();
    this.#loadVariantImageMap();
    this.#loadVariantsJson();
    this.#setupIntersectionObservers();

    const { signal } = this.#abortController;

    // ✅ Belangrijk: bind click altijd met een normale listener.
    // Zo werkt het ook als on:click binding niet (goed) wordt verwerkt.
    this.refs.addToCartButton?.addEventListener(
      'click',
      (e) => {
        e.preventDefault();
        this.handleAddToCartClick();
      },
      { signal }
    );

    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#bindVariantIdWatcher();
    this.#bindStickyOptionSelects();
    this.#getInitialQuantity();
    this.#syncStickyDisabledState();

    const id = this.dataset.currentVariantId || this.#getCurrentVariantIdFromForm();
    this.#updateStickyImageFromMap(id);
    this.#syncStickySelectsToVariantId(id);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#footerIntersectionObserver?.disconnect();

    if (this.#variantIdObserver) {
      this.#variantIdObserver.disconnect();
      this.#variantIdObserver = null;
    }

    if (this.#variantSyncInterval) {
      clearInterval(this.#variantSyncInterval);
      this.#variantSyncInterval = null;
    }

    this.#abortController.abort();

    if (this.#scrollClickTimeout) clearTimeout(this.#scrollClickTimeout);
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  handleAddToCartClick = async () => {
    const mainButton = this.#getMainAtcButton();
    if (!mainButton) return;

    const productForm = this.#getProductFormNative() || mainButton.closest('form');
    const target = productForm || mainButton;

    const rect = target.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const targetY = rect.top + scrollTop - 100;

    window.scrollTo({ top: targetY, behavior: 'smooth' });

    if (this.#scrollClickTimeout) clearTimeout(this.#scrollClickTimeout);
    this.#scrollClickTimeout = setTimeout(async () => {
      mainButton.dataset.puppet = 'true';
      mainButton.click();

      const cartIcon = document.querySelector('.header-actions__cart-icon');

      if (this.refs.addToCartButton.dataset.added !== 'true') {
        this.refs.addToCartButton.dataset.added = 'true';
      }

      if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;

      if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

      const flyToCartElement = document.createElement('fly-to-cart');
      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = this.refs.productImage;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);

      await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);

      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 800);
    }, 400);
  };

  #ensureStickyBarInBody() {
    const bar = this.refs?.stickyBar;
    if (!bar) return;
    if (bar.parentElement !== document.body) {
      document.body.appendChild(bar);
    }
  }

  #setupIntersectionObservers() {
    const productFormComponent = this.#getProductFormComponent();
    if (!productFormComponent) return;

    const buyButtonsBlock = productFormComponent.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    this.#targetAddToCartButton = this.#getMainAtcButton();

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      if (!entry.isIntersecting) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom <= 0) {
          if (!this.#isStuck) this.#showStickyBar();
        } else {
          if (this.#isStuck) this.#hideStickyBar();
        }
      } else {
        if (this.#isStuck) this.#hideStickyBar();
      }
    });

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
  }

  #bindVariantIdWatcher() {
    const form = this.#getProductFormNative();
    if (!form) return;

    const variantIdInput = form.querySelector('input[name="id"]');
    if (!variantIdInput) return;

    const apply = () => {
      const id = variantIdInput.value;
      if (id) this.dataset.currentVariantId = id;

      this.#updateStickyImageFromMap(id);
      this.#syncStickyDisabledState();
      this.#syncStickySelectsToVariantId(id);
    };

    apply();

    // MutationObserver op attribute "value" is niet altijd voldoende (property kan wijzigen zonder attribute).
    // We laten hem staan, maar voegen ook een lichte sync-interval toe.
    this.#variantIdObserver = new MutationObserver(apply);
    this.#variantIdObserver.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });

    const { signal } = this.#abortController;
    variantIdInput.addEventListener('change', apply, { signal });
    variantIdInput.addEventListener('input', apply, { signal });

    // Extra: sync elke 300ms zolang component leeft (licht en betrouwbaar).
    if (!this.#variantSyncInterval) {
      this.#variantSyncInterval = setInterval(() => {
        if (signal.aborted) return;
        const current = this.dataset.currentVariantId || '';
        const live = variantIdInput.value || '';
        if (live && live !== current) {
          apply();
        }
      }, 300);
    }
  }

  #bindStickyOptionSelects() {
    if (this.#stickySelectsBound) return;

    const selects = this.#getStickyOptionSelects();
    if (!selects.length) return;

    const { signal } = this.#abortController;

    selects.forEach((select) => {
      select.addEventListener(
        'change',
        () => {
          this.#applyVariantFromStickySelects();
        },
        { signal }
      );
    });

    this.#stickySelectsBound = true;
  }

  #getStickyOptionSelects() {
    const bar = this.refs?.stickyBar;
    if (!bar) return [];
    return Array.from(bar.querySelectorAll('select[data-option-index]'));
  }

  #applyVariantFromStickySelects() {
    const selects = this.#getStickyOptionSelects();
    if (!selects.length) return;

    const chosen = [];
    selects.forEach((s) => {
      chosen[Number(s.dataset.optionIndex)] = s.value;
    });

    const variant = this.#findVariantByOptions(chosen);
    if (!variant) return;

    const picker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (picker) {
      const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
      chosen.forEach((val, idx) => {
        const fs = fieldsets[idx];
        if (!fs) return;
        const input = fs.querySelector(`input[value="${CSS.escape(val)}"]`);
        if (input && !input.checked) input.click();
      });
      return;
    }

    const form = this.#getProductFormNative();
    if (!form) return;

    const variantIdInput = form.querySelector('input[name="id"]');
    if (!variantIdInput) return;

    variantIdInput.value = String(variant.id);
    variantIdInput.dispatchEvent(new Event('input', { bubbles: true }));
    variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #findVariantByOptions(chosen) {
    if (!this.#variants || !Array.isArray(this.#variants)) return null;

    for (let i = 0; i < this.#variants.length; i++) {
      const v = this.#variants[i];
      if (!v || !Array.isArray(v.options)) continue;

      let ok = true;
      for (let j = 0; j < chosen.length; j++) {
        if (chosen[j] == null) continue;
        if (v.options[j] !== chosen[j]) {
          ok = false;
          break;
        }
      }
      if (ok) return v;
    }

    return null;
  }

  #findVariantById(id) {
    if (!this.#variants || !Array.isArray(this.#variants)) return null;
    const sid = String(id);
    for (let i = 0; i < this.#variants.length; i++) {
      const v = this.#variants[i];
      if (!v) continue;
      if (String(v.id) === sid) return v;
    }
    return null;
  }

  #syncStickySelectsToVariantId(id) {
    if (!id) return;

    const v = this.#findVariantById(id);
    if (!v || !Array.isArray(v.options)) return;

    const selects = this.#getStickyOptionSelects();
    if (!selects.length) return;

    selects.forEach((s) => {
      const idx = Number(s.dataset.optionIndex);
      const val = v.options[idx];
      if (typeof val === 'string') s.value = val;
    });
  }

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = String(variant.id);
    }

    this.#ensureStickyBarInBody();
    this.#targetAddToCartButton = this.#getMainAtcButton();

    this.#bindStickyOptionSelects();

    this.#updateButtonText();
    this.#syncStickyDisabledState();

    const id = this.dataset.currentVariantId || this.#getCurrentVariantIdFromForm();
    this.#updateStickyImageFromMap(id);
    this.#syncStickySelectsToVariantId(id);

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
  };

  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = String(variantId);

    const id = String(variantId);
    this.#updateStickyImageFromMap(id);
    this.#syncStickySelectsToVariantId(id);
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => option.value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  #handleCartAddComplete = () => {
    const mainButton = this.#getMainAtcButton();
    if (mainButton) mainButton.dataset.puppet = 'false';
  };

  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
    this.refs.stickyBar.classList.add('show');
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
    this.refs.stickyBar.classList.remove('show');
  }

  #getProductFormComponent() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getProductFormNative() {
    const comp = this.#getProductFormComponent();
    if (!comp) return null;
    return comp.querySelector('form[data-type="add-to-cart-form"]') || comp.querySelector('form.shopify-product-form');
  }

  #getMainAtcButton() {
    const form = this.#getProductFormNative();
    if (!form) return null;

    return (
      form.querySelector('.product-form-buttons .add-to-cart-button') ||
      form.querySelector('button[type="submit"], button[name="add"]')
    );
  }

  #getCurrentVariantIdFromForm() {
    const form = this.#getProductFormNative();
    if (!form) return '';
    const input = form.querySelector('input[name="id"]');
    return input?.value || '';
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;
    quantityNumber.textContent = String(this.#currentQuantity);

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  #syncStickyDisabledState() {
    const mainButton = this.#getMainAtcButton();
    if (!mainButton) return;

    const disabled = !!mainButton.disabled;
    this.refs.addToCartButton.disabled = disabled;
    this.dataset.variantAvailable = disabled ? 'false' : 'true';
  }

  #loadVariantsJson() {
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    const el =
      sectionElement.querySelector(`script#sticky-variants-${sectionId}`) ||
      document.getElementById(`sticky-variants-${sectionId}`);

    if (!el) return;

    try {
      const parsed = JSON.parse(el.textContent.trim());
      if (Array.isArray(parsed)) this.#variants = parsed;
    } catch (e) {
      this.#variants = null;
    }
  }

  #loadVariantImageMap() {
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    const withinSection = sectionElement.querySelector(`script#variant-image-map-${sectionId}`);
    const anywhere = document.getElementById(`variant-image-map-${sectionId}`);

    const mapEl = withinSection || anywhere;
    if (!mapEl) return;

    try {
      const parsed = JSON.parse(mapEl.textContent.trim());
      if (parsed && typeof parsed === 'object') this.#variantImages = parsed;
    } catch (e) {
      this.#variantImages = null;
    }
  }

  #updateStickyImageFromMap(variantId) {
    if (!this.refs.productImage) return;
    if (!this.#variantImages) return;
    if (!variantId) return;

    const url = this.#variantImages[String(variantId)];
    if (!url) return;

    this.refs.productImage.src = url;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}